import { useEffect, useId, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ChevronDown, Sparkles } from 'lucide-react';
import { useTranslation } from '../../i18n/useTranslation';
import { Alert } from '../atoms/Alert/Alert';
import { Badge } from '../atoms/Badge/Badge';
import type { BadgeTone } from '../atoms/Badge/Badge.types';
import { Button } from '../atoms/Button/Button';
import { Card } from '../atoms/Card/Card';
import { EmptyState } from '../atoms/EmptyState';
import { Inline } from '../atoms/Inline/Inline';
import { LiveRegion } from '../atoms/LiveRegion/LiveRegion';
import { Stack } from '../atoms/Stack/Stack';
import { Heading } from '../atoms/Typography/Heading';
import { Text } from '../atoms/Typography/Text';
import { LoadErrorState } from '../feedback/LoadErrorState/LoadErrorState';
import { AiUsageMeter } from '../aiUsage/AiUsageMeter';
import { AiLimitErrorMessage } from '../aiUsage/AiLimitNotice';
import { getAiLimitErrorKind } from '../../services/aiUsageService';
import { invalidateAiUsage, useAiUsage } from '../../hooks/useAiUsage';
import {
  MIN_POINTS_FOR_EVALUATION,
  evaluateLightSurvey,
  getLightEvaluation,
  latestLightEvaluationKey,
  lightEvaluationKey,
  type AreaAssessment,
  type LightEvaluation,
  type LightZoneStats,
  type LightZones,
  type LightVerdict,
  type Priority,
} from '../../services/lightSurveyService';
import './Lighting.css';

/** Verdict → Badge tone. The Badge text always carries the meaning. */
export const VERDICT_TONE: Readonly<Record<LightVerdict, BadgeTone>> = {
  adequate: 'success',
  too_low: 'warning',
  too_high: 'warning',
  uneven: 'warning',
  insufficient_data: 'neutral',
};

const AREA_TONE: Readonly<Record<AreaAssessment, BadgeTone>> = { adequate: 'success', too_low: 'warning', too_high: 'warning' };
const PRIORITY_TONE: Readonly<Record<Priority, BadgeTone>> = { high: 'danger', medium: 'warning', low: 'neutral' };

export function errorCode(error: unknown): string | null {
  const code = (error as { code?: unknown } | null)?.code;
  return typeof code === 'string' ? code : null;
}

/**
 * "PROVIDER_FAILED · OUTPUT_TRUNCATED" / "API_ERROR · HTTP 504": the server's code and its
 * short detail, shown under a failed evaluation so a screenshot tells what went wrong.
 */
export function errorReference(error: unknown): string | null {
  const code = errorCode(error);
  if (!code) return null;
  const e = error as { status?: unknown; data?: { error?: { detail?: unknown } } };
  const detail = typeof e.data?.error?.detail === 'string' ? e.data.error.detail : null;
  const status = typeof e.status === 'number' ? `HTTP ${e.status}` : null;
  return [code, detail ?? (code === 'API_ERROR' ? status : null)].filter(Boolean).join(' · ');
}

/** Name of a language in the UI language ("Englisch"), or the code when unknown. */
export function languageName(code: string, uiLocale: string): string {
  try {
    return new Intl.DisplayNames([uiLocale], { type: 'language' }).of(code) ?? code;
  } catch {
    return code;
  }
}

function GroupBadges({ groups }: { groups: ReadonlyArray<'soft' | 'lps' | 'sps'> }) {
  const { t } = useTranslation();
  if (groups.length === 0) return <Text size="sm" color="muted">{t('lighting.evaluation.zones.none')}</Text>;
  return (
    <Inline gap="1">
      {groups.map((g) => (
        <Badge key={g} tone="info">{t(`lighting.evaluation.groupShort.${g}`)}</Badge>
      ))}
    </Inline>
  );
}

/**
 * A long part of the evaluation behind a native <details> disclosure: closed by default so
 * the card stays short on a phone; keyboard and screen readers get the expanded state for free.
 */
export function CollapsibleSection({ title, count, testId, children }: { title: string; count: number; testId?: string; children: ReactNode }) {
  return (
    <details className="lighting__collapsible" data-testid={testId}>
      <summary className="lighting__collapsible-summary">
        <Heading level={3} as="span" className="lighting__collapsible-title">{title}</Heading>
        <Badge tone="neutral">{count}</Badge>
        <ChevronDown size={18} className="lighting__collapsible-icon" aria-hidden="true" />
      </summary>
      <div className="lighting__collapsible-body">{children}</div>
    </details>
  );
}

/** The computed light zones (not AI): across the tank and by height, median PAR and the groups each suits. */
function LightZonesView({ zones }: { zones: LightZones }) {
  const { t } = useTranslation();
  const row = (z: LightZoneStats) => (
    <li key={z.band} className="lighting__evaluation-item">
      <Inline gap="2" align="center" justify="between">
        <Text weight="semibold">{t(`lighting.evaluation.zones.${z.band}`)}</Text>
        <GroupBadges groups={z.suits} />
      </Inline>
      <Text size="sm" color="secondary">
        {t('lighting.evaluation.zones.values', { median: z.medianPar, min: z.minPar, max: z.maxPar, points: z.pointLabels.join(', ') })}
      </Text>
    </li>
  );
  return (
    <CollapsibleSection title={t('lighting.evaluation.zones.title')} count={zones.horizontal.length + zones.levels.length} testId="light-zones">
      <Stack gap="3">
        <Text size="sm" color="secondary">
          {t(zones.expectedGradient ? 'lighting.evaluation.zones.expected' : 'lighting.evaluation.zones.unexpected')}
        </Text>
        <ul className="lighting__list">{zones.horizontal.map(row)}</ul>
        <ul className="lighting__list">{zones.levels.map(row)}</ul>
        {zones.primeSpsLabels.length > 0 && (
          <Text size="sm">{t('lighting.evaluation.zones.prime', { points: zones.primeSpsLabels.join(', ') })}</Text>
        )}
      </Stack>
    </CollapsibleSection>
  );
}

/** The evaluation itself: verdict, summary, areas, corals, recommendations. */
export function LightEvaluationResultView({ evaluation }: { evaluation: LightEvaluation }) {
  const { t, i18n } = useTranslation();
  const { result } = evaluation;
  const lang = evaluation.language;
  const date = new Intl.DateTimeFormat(i18n.language, { dateStyle: 'medium', timeStyle: 'short' }).format(new Date(evaluation.createdAt));
  const uiLanguage = (i18n.language ?? 'en').split('-')[0];
  const step = result.intensityChangePercent;

  return (
    <Stack gap="6">
      <Stack gap="2">
        <Inline gap="2" align="center">
          <Badge tone={VERDICT_TONE[result.verdict]} data-testid="light-evaluation-verdict">
            {t(`lighting.evaluation.verdict.${result.verdict}`)}
          </Badge>
          <Badge tone="ai">{t(`lighting.evaluation.confidence.${result.confidence}`)}</Badge>
        </Inline>
        <Text lang={lang} data-testid="light-evaluation-summary">{result.summary}</Text>
        <Text size="sm" color="muted">
          {t('lighting.evaluation.meta', { date, points: result.pointCount, corals: result.coralCount })}
          {` · ${String(t(result.usedPhoto ? 'lighting.evaluation.withPhoto' : 'lighting.evaluation.withoutPhoto'))}`}
        </Text>
        {lang !== uiLanguage && (
          <Alert tone="info" compact live="off">{t('lighting.evaluation.otherLanguage', { language: languageName(lang, i18n.language) })}</Alert>
        )}
      </Stack>

      {step !== null && (
        <Alert tone="info" live="off" title={t('lighting.evaluation.intensityTitle')} titleLevel={3}>
          {t(step < 0 ? 'lighting.evaluation.intensityDown' : 'lighting.evaluation.intensityUp', { value: Math.abs(step) })}
          {result.intensityStepCapped && ` ${String(t('lighting.evaluation.intensityCapped'))}`}
        </Alert>
      )}

      {result.intensityStepWithheld && (
        <Text size="sm" color="secondary" data-testid="light-evaluation-withheld">{t('lighting.evaluation.intensityWithheld')}</Text>
      )}

      {result.zones && <LightZonesView zones={result.zones} />}

      {result.areas.length > 0 && (
        <CollapsibleSection title={t('lighting.evaluation.areasTitle')} count={result.areas.length} testId="light-evaluation-areas">
          <ul className="lighting__list" lang={lang}>
            {result.areas.map((a, i) => (
              <li key={`${a.area}-${i}`} className="lighting__evaluation-item">
                <Inline gap="2" align="center">
                  <Text weight="semibold">{a.area}</Text>
                  <Badge tone={AREA_TONE[a.assessment]}>{t(`lighting.evaluation.area.${a.assessment}`)}</Badge>
                </Inline>
                {a.suitableGroups && a.suitableGroups.length > 0 && <GroupBadges groups={a.suitableGroups} />}
                {a.detail && <Text size="sm" color="secondary">{a.detail}</Text>}
                {a.pointLabels.length > 0 && (
                  <Text size="sm" color="muted">{t('lighting.evaluation.points', { points: a.pointLabels.join(', ') })}</Text>
                )}
              </li>
            ))}
          </ul>
        </CollapsibleSection>
      )}

      {result.corals.length > 0 && (
        <CollapsibleSection title={t('lighting.evaluation.coralsTitle')} count={result.corals.length}>
          <ul className="lighting__list" data-testid="light-evaluation-corals">
            {result.corals.map((c) => (
              <li key={c.coralId} className="lighting__evaluation-item">
                <Inline gap="2" align="center">
                  <Text weight="semibold">{t('lighting.evaluation.coralName', { number: c.coralNumber, name: c.name })}</Text>
                  <Badge tone={c.placement === 'well_placed' ? 'success' : c.placement === 'uncertain' ? 'neutral' : 'warning'}>
                    {t(`lighting.evaluation.placement.${c.placement}`)}
                  </Badge>
                </Inline>
                <Text size="sm" color="secondary">{t(`lighting.evaluation.visual.${c.visualCondition}`)}</Text>
                {c.comment && <Text size="sm" lang={lang}>{c.comment}</Text>}
              </li>
            ))}
          </ul>
        </CollapsibleSection>
      )}

      {result.recommendations.length > 0 && (
        <Stack gap="3">
          <Heading level={3}>{t('lighting.evaluation.recommendationsTitle')}</Heading>
          <ol className="lighting__list" data-testid="light-evaluation-recommendations">
            {result.recommendations.map((r, i) => (
              <li key={`${r.action}-${i}`} className="lighting__evaluation-item">
                <Inline gap="2" align="center">
                  <Badge tone={PRIORITY_TONE[r.priority]}>{t(`lighting.evaluation.priority.${r.priority}`)}</Badge>
                  <Text size="sm" color="muted">{t(`lighting.evaluation.action.${r.action}`)}</Text>
                </Inline>
                <Text lang={lang}>{r.text}</Text>
              </li>
            ))}
          </ol>
        </Stack>
      )}

      <Text variant="caption" color="muted">{t('lighting.evaluation.disclaimer')}</Text>
    </Stack>
  );
}

export interface LightEvaluationCardProps {
  aquariumId: string;
  surveyId: string;
  /** Measured points of the survey: the agent needs MIN_POINTS_FOR_EVALUATION. */
  pointCount: number;
}

/**
 * Lighting evaluation agent on the light map page: once the survey is measured,
 * the AI judges the overlay picture, the photo and the measurement series —
 * adequate or not, where too little / too much, coral placement, what to change.
 * Renders loading, load error, "measure more first", not yet evaluated,
 * evaluating, error and result states.
 */
/** While an evaluation is generated in the background, the card re-reads it this often… */
export const PENDING_POLL_MS = 5_000;
/** …and gives up after this long (the server stops the AI call after 3 minutes). */
export const PENDING_GIVE_UP_MS = 4 * 60_000;

export function LightEvaluationCard({ aquariumId, surveyId, pointCount }: LightEvaluationCardProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const headingId = useId();
  const errorRef = useRef<HTMLDivElement>(null);
  const { exceeded: aiCreditsUsedUp } = useAiUsage();
  // A long evaluation answers "pending" and finishes in the background: until a new
  // evaluation (another id than before the click) is stored, the card re-reads it.
  const [pending, setPending] = useState<{ previousId: string | null; since: number } | null>(null);
  const [pendingGaveUp, setPendingGaveUp] = useState(false);
  const query = useQuery({
    queryKey: lightEvaluationKey(aquariumId, surveyId),
    queryFn: () => getLightEvaluation(aquariumId, surveyId),
    enabled: Boolean(aquariumId && surveyId),
    refetchInterval: pending ? PENDING_POLL_MS : false,
  });
  const run = useMutation({
    mutationFn: (force: boolean) => evaluateLightSurvey(aquariumId, surveyId, force),
    onMutate: () => {
      setPendingGaveUp(false);
      return { previousId: query.data?.id ?? null };
    },
    onSuccess: (evaluation, _force, context) => {
      if (!evaluation) {
        setPending({ previousId: context?.previousId ?? null, since: Date.now() });
        return;
      }
      queryClient.setQueryData(lightEvaluationKey(aquariumId, surveyId), evaluation);
      void queryClient.invalidateQueries({ queryKey: latestLightEvaluationKey(aquariumId) });
      invalidateAiUsage(queryClient);
    },
  });
  useEffect(() => {
    if (!pending) return;
    const latest = query.data;
    if (latest && latest.id !== pending.previousId) {
      setPending(null);
      void queryClient.invalidateQueries({ queryKey: latestLightEvaluationKey(aquariumId) });
      invalidateAiUsage(queryClient);
    } else if (Date.now() - pending.since > PENDING_GIVE_UP_MS) {
      setPending(null);
      setPendingGaveUp(true);
    }
  }, [pending, query.data, query.dataUpdatedAt, queryClient, aquariumId]);
  // A saved change on the survey page invalidates ['light-surveys', aquariumId, surveyId], which
  // includes this key: `outdated` is re-read after every change.
  const enoughPoints = pointCount >= MIN_POINTS_FOR_EVALUATION;
  useEffect(() => {
    if (run.isError || pendingGaveUp) errorRef.current?.focus();
  }, [run.isError, pendingGaveUp]);

  const evaluation = query.data ?? null;
  const evaluate = (force: boolean): void => run.mutate(force);
  const busy = run.isPending || pending !== null;
  const disabled = busy || aiCreditsUsedUp || !enoughPoints;

  let runError = null;
  if (pendingGaveUp) {
    runError = <Alert ref={errorRef} tabIndex={-1} compact>{t('lighting.evaluation.error')}</Alert>;
  } else if (run.isError) {
    const code = errorCode(run.error);
    runError = getAiLimitErrorKind(run.error) ? (
      <AiLimitErrorMessage ref={errorRef} tabIndex={-1} error={run.error} suppressExceeded={aiCreditsUsedUp} />
    ) : (
      <Alert ref={errorRef} tabIndex={-1} compact>
        {t(code === 'NOT_ENOUGH_POINTS' ? 'lighting.evaluation.errorPoints' : code === 'RATE_LIMITED' ? 'lighting.evaluation.errorRate' : 'lighting.evaluation.error')}
        {code !== 'NOT_ENOUGH_POINTS' && code !== 'RATE_LIMITED' && errorReference(run.error) && (
          <Text as="span" size="sm" className="lighting__error-reference" data-testid="light-evaluation-error-reference">
            {t('lighting.evaluation.errorReference', { reference: errorReference(run.error) })}
          </Text>
        )}
      </Alert>
    );
  }

  let body;
  if (query.isPending) {
    body = <LiveRegion busy><Text color="muted">{t('lighting.evaluation.loading')}</Text></LiveRegion>;
  } else if (query.isError) {
    body = <LoadErrorState title={t('lighting.evaluation.loadError')} onRetry={() => void query.refetch()} retryLabel={t('lighting.retry')} titleLevel={3} />;
  } else if (!evaluation) {
    body = enoughPoints ? (
      <EmptyState
        icon={<Sparkles size={32} />}
        headline={t('lighting.evaluation.emptyTitle')}
        description={t('lighting.evaluation.emptyDescription')}
        actionLabel={busy ? undefined : t('lighting.evaluation.run')}
        onAction={aiCreditsUsedUp ? undefined : () => evaluate(false)}
        headingLevel={3}
      />
    ) : (
      <EmptyState
        icon={<Sparkles size={32} />}
        headline={t('lighting.evaluation.needPointsTitle')}
        description={t('lighting.evaluation.needPoints', { min: MIN_POINTS_FOR_EVALUATION, count: pointCount })}
        headingLevel={3}
      />
    );
  } else {
    body = (
      <Stack gap="4">
        {evaluation.outdated && (
          <Alert tone="info" live="off" compact data-testid="light-evaluation-outdated">
            {t('lighting.evaluation.outdated')}
          </Alert>
        )}
        <LightEvaluationResultView evaluation={evaluation} />
      </Stack>
    );
  }

  return (
    <Card as="section" aria-labelledby={headingId} data-testid="light-evaluation-card">
      <Stack gap="4">
        <Inline justify="between" gap="3" align="center">
          <Inline gap="2" align="center" wrap={false}>
            <span className="lighting__evaluation-icon" aria-hidden="true"><Sparkles size={20} /></span>
            <Heading level={3} id={headingId}>{t('lighting.evaluation.cardTitle')}</Heading>
          </Inline>
          {evaluation && (
            <Button
              variant={evaluation.outdated ? 'primary' : 'secondary'}
              size="sm"
              onClick={() => evaluate(!evaluation.outdated)}
              isLoading={busy}
              disabled={disabled}
            >
              {t(evaluation.outdated ? 'lighting.evaluation.rerunOutdated' : 'lighting.evaluation.rerun')}
            </Button>
          )}
        </Inline>
        <Text color="secondary" size="sm">{t('lighting.evaluation.intro')}</Text>
        <AiUsageMeter variant="compact" />
        {run.isPending && (
          <LiveRegion busy>
            <Text color="muted">{t('lighting.evaluation.running')}</Text>
          </LiveRegion>
        )}
        {pending && (
          <LiveRegion busy>
            <Text color="muted" data-testid="light-evaluation-pending">{t('lighting.evaluation.pending')}</Text>
          </LiveRegion>
        )}
        {runError}
        {body}
      </Stack>
    </Card>
  );
}

export default LightEvaluationCard;
